import { Filter, IActiveFilters, NumberRange, SelectList, SelectListMulti } from 'types';
import { useEffect, useState, useCallback } from 'react';

import { FilterType } from './FilterType';
import { getList, getFilteredList } from '../filterFactoryMethods';

export interface IProps {
  filter: Filter;
  search: string;
  activeFilters: IActiveFilters;
}

export interface IDispatchProps {
  saveFilter: (key: string, values: string[] | number[]) => void;
}

export const Content = ({ filter, search, activeFilters, saveFilter }: IProps & IDispatchProps) => {
  const [list, setList] = useState<SelectList | NumberRange | SelectListMulti>(getList(filter));

  const handleFilterSearch = useCallback((callbackFilter: Filter, callbackSeearch: string) => {
    setList(getFilteredList(callbackFilter, callbackSeearch));
  }, []);

  useEffect(() => {
    handleFilterSearch(filter, search);
  }, [search, filter, handleFilterSearch]);

  /**
   * Applies the current filter by
   * 1) identifying which selected values are not already in the active filters.
   * 2) only pushing the new values alongwith the existing values
   *
   * If we are selecting many -> we find all the possible values from the filter list.
   * -> then we find active filters for the filter key -> then we filter the active filters
   * for values that arent in the filter list (these are new values).
   *
   * Otherwise if we dont have any existing values, or we are selecting one, we pass
   * all the values to saveFilter.
   */
  const applyFilter = (key: string, values: string[] | number[] | string[][]): void => {
    const valuesFlat = values?.flat();

    if (search?.length && activeFilters[key]?.length) {
      if (filter.options?.SELECT_MANY || filter.options?.SELECT_MANY_MULTI) {
        const listValuesRaw: (string | number | string[])[] = list
          .map(item => item.value)
          .filter(value => value !== undefined);
        const listValues: (string | number)[] = listValuesRaw.flat();

        const activeFiltersForKey: string[] = activeFilters[key];
        // New filter values are list values not present in activeFilters
        const newValues = activeFiltersForKey.filter(val => !listValues.includes(val));

        if (valuesFlat.length) {
          const scopedValues = valuesFlat.filter(val => listValues.includes(val));
          const filteredValues = [...new Set([...newValues, ...scopedValues])] as string[];
          return saveFilter(filter.key, filteredValues);
        }

        return saveFilter(key, newValues);
      }
    }
    return saveFilter(key, valuesFlat as string[] | number[]);
  };

  return (
    <>
      <FilterType filter={filter} list={list} activeFilters={activeFilters} saveFilter={applyFilter} />
    </>
  );
};
